import { authHeaders } from "../../utils/auth";

const API_BASE = process.env.REACT_APP_BACKEND_API_URL || "";

/** Where the admin's typed name is remembered; sent with every write (the team shares one login). */
export const ADMIN_NAME_KEY = "mrehabAdminName";
const MAX_ADMIN_NAME = 60;

export interface OverviewTiles {
  activeThisWeek: number;
  patients: number;
  sessionsThisWeek: number;
  sessionsLastWeek: number;
  /** One entry per app seen: 1.x "mRehab" (open testing), 2.x "mRehab v2" (internal testing). */
  apps: { app: string; newestVersion: string; patients: number; olderVersionPatients: number }[];
  lastUpload: string | null;
}

export interface PatientRow {
  email: string;
  name: string | null;
  tags: string[];
  lastActive: string | null;
  sessionsThisWeek: number;
  totalSessions: number;
  appVersion: string | null;
  /** "mRehab" (1.x) or "mRehab v2" (2.x); null for junk versions. */
  app: string | null;
  /** Older than the newest version seen for the same app. */
  olderVersion: boolean;
  /** Days with a session in the last 28 (New York dates): how regularly they use the app. */
  activeDays28: number;
}

export interface Overview {
  tiles: OverviewTiles;
  patients: PatientRow[];
  testAccounts: PatientRow[];
  /** Session usernames with no account (deleted accounts, early builds that stored a name). Never counted. */
  noAccount: PatientRow[];
  updatedAt: string;
}

export interface PatientDetail {
  email: string;
  profile: Record<string, unknown> & {
    givenName?: string | null;
    familyName?: string | null;
    birthYear?: number | null;
    affectedSide?: string | null;
    gender?: string | null;
    genderSelfDescribe?: string | null;
    address?: Record<string, string> | string | null;
    tourCompletedAt?: string | null;
  };
  tags: string[];
  isTest: boolean;
  groups: string[];
  latestDevice: { appVersion?: string | null; modelName?: string | null; osName?: string | null; osVersion?: string | null } | null;
  lastActive: string | null;
  totalSessions: number;
  sessionsThisWeek: number;
  sharingWith: string[];
  account: { createdAt: string | null; status: string | null; enabled: boolean } | null;
}

export interface SessionDay {
  date: string;
  count: number;
}

export interface FileItem {
  artifactId: string | null;
  type: "baseline" | "rep" | "aggregate" | string | null;
  repIndex: number | null;
  s3Key: string | null;
  status: string | null;
}

export interface SessionFilesInfo {
  linked: boolean;
  expected: number | null;
  present: number;
  items: FileItem[];
  /** Newer session (since file linking began) with no files at all: old app build or lost upload. */
  noneReceived: boolean;
}

export interface DeviceDetails {
  appVersion: string | null;
  modelName: string | null;
  manufacturer: string | null;
  osName: string | null;
  osVersion: string | null;
  totalMemory: number | null;
}

export interface SessionRow {
  sessionId: string;
  timestamp: string;
  exercise: string | null;
  hand: string | null;
  reps: number | null;
  scores: unknown;
  avgJerk: number | null;
  durationSec: number | null;
  device: DeviceDetails | null;
  files: SessionFilesInfo;
}

export interface DaySessions {
  summary: { sessions: number; exercises: number; reps: number; minutes: number };
  sessions: SessionRow[];
}

export interface GraphData {
  key: string;
  /** The CSV's first line as key/value pairs (Workout, Duration, Durations#n, JerkScore#n, AppVersion...). */
  metadata: Record<string, string>;
  headers: string[];
  timeColumn: string | null;
  xValues: number[];
  series: { name: string; values: (number | null)[] }[];
  parseError?: string | null;
  /** From the CSV's "# Thresholds:" line: one horizontal line per descriptor, drawn on its series. */
  thresholdOverlay?: {
    logicType?: string;
    detectorSignals?: string[];
    descriptors?: { id?: string; label?: string; note?: string; series?: string; value?: number; direction?: string }[];
  } | null;
}

export interface AuditRow {
  at: string;
  action: string;
  target: string;
  detail: Record<string, unknown>;
  loginEmail: string;
  actorName: string;
}

export function storedAdminName(): string {
  try {
    return (window.localStorage.getItem(ADMIN_NAME_KEY) || "").trim();
  } catch {
    return "";
  }
}

export function saveAdminName(name: string): string {
  const clean = name.trim().slice(0, MAX_ADMIN_NAME);
  try {
    if (clean) window.localStorage.setItem(ADMIN_NAME_KEY, clean);
    else window.localStorage.removeItem(ADMIN_NAME_KEY);
  } catch {
    /* storage blocked: the name lasts for this action only */
  }
  return clean;
}

/** The stored name, or ask for it once. Returns "" when the admin cancels. */
export function ensureAdminName(): string {
  const stored = storedAdminName();
  if (stored) return stored;
  const typed = window.prompt("Your name (remembered in this browser and recorded with every change you make):");
  return typed ? saveAdminName(typed) : "";
}

function message(status: number, detail: unknown): string {
  if (status === 401) return "Your session has expired. Please sign in again.";
  if (status === 403) return "This page is for mRehab admins only.";
  return typeof detail === "string" ? detail : `Request failed (${status})`;
}

// The name is percent-encoded: browsers refuse header values outside Latin-1 (e.g. "Tân", "Ω"),
// which would make every admin change fail. The backend decodes it.
async function request(path: string, init: RequestInit = {}): Promise<Response> {
  const method = (init.method || "GET").toUpperCase();
  const nameHeader: Record<string, string> = method === "GET" ? {} : { "X-Admin-Name": encodeURIComponent(storedAdminName()) };
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", ...authHeaders(), ...nameHeader, ...(init.headers || {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(message(res.status, body?.detail));
  }
  return res;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await request(path, init);
  return (await res.json().catch(() => ({}))) as T;
}

const enc = encodeURIComponent;

export function getOverview(opts: { tag?: string; includeTest?: boolean } = {}): Promise<Overview> {
  const params = new URLSearchParams();
  if (opts.tag) params.set("tag", opts.tag);
  if (opts.includeTest) params.set("includeTest", "true");
  const qs = params.toString().replace(/\+/g, "%20");
  return call<Overview>(`api/admin/overview${qs ? `?${qs}` : ""}`);
}

export function getPatient(email: string): Promise<PatientDetail> {
  return call<PatientDetail>(`api/admin/patients/${enc(email)}`);
}

export function getDays(email: string, start: string, end: string): Promise<SessionDay[]> {
  return call<SessionDay[]>(`api/admin/patients/${enc(email)}/days?start=${start}&end=${end}`);
}

export function getSessions(email: string, date: string, exercise?: string): Promise<DaySessions> {
  const ex = exercise ? `&exercise=${enc(exercise)}` : "";
  return call<DaySessions>(`api/admin/patients/${enc(email)}/sessions?date=${date}${ex}`);
}

export function getGraph(key: string): Promise<GraphData> {
  return call<GraphData>(`api/admin/files/graph?key=${enc(key)}`);
}

export function getFileUrl(key: string): Promise<string> {
  return call<{ url: string }>(`api/admin/files/url?key=${enc(key)}`).then((r) => r.url);
}

export function zipUrl(sessionId: string, email: string): string {
  return `api/admin/sessions/${enc(sessionId)}/zip?email=${enc(email)}`;
}

/** Fetch a session's zip with the login token and hand it to the browser as a download.
 * The filename is chosen here: cross-origin, the browser hides Content-Disposition from fetch. */
export async function downloadZip(sessionId: string, email: string, filename: string): Promise<void> {
  const res = await request(zipUrl(sessionId, email));
  saveBlob(await res.blob(), filename);
}

/** Follow a signed S3 link that is served as an attachment: the browser saves it and stays on this page. */
export function followDownloadLink(url: string): void {
  window.location.assign(url);
}

export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function setTags(email: string, tags: string[]): Promise<string[]> {
  return call<{ tags: string[] }>(`api/admin/accounts/${enc(email)}/tags`, {
    method: "PUT",
    body: JSON.stringify({ tags }),
  }).then((r) => r.tags);
}

export function setTestFlag(email: string, isTest: boolean): Promise<boolean> {
  return call<{ isTest: boolean }>(`api/admin/accounts/${enc(email)}/test`, {
    method: "PUT",
    body: JSON.stringify({ isTest }),
  }).then((r) => r.isTest);
}

export function setProvider(email: string, grant: boolean): Promise<string[]> {
  return call<{ groups: string[] }>(`api/admin/accounts/${enc(email)}/provider`, {
    method: "POST",
    body: JSON.stringify({ grant }),
  }).then((r) => r.groups);
}

export function removeShare(email: string, viewer: string): Promise<string[]> {
  return call<{ sharingWith: string[] }>(`api/admin/accounts/${enc(email)}/sharing/${enc(viewer)}`, {
    method: "DELETE",
  }).then((r) => r.sharingWith);
}

export function getAudit(limit = 50): Promise<AuditRow[]> {
  return call<{ audit: AuditRow[] }>(`api/admin/audit?limit=${limit}`).then((r) => r.audit);
}

export async function backendHealthy(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}health`);
    return res.ok;
  } catch {
    return false;
  }
}
